import React from "react";
import { AssessmentQuestion } from "./questionTypes";
import { AnswerData, QuestionAnswer, isRadioAnswer } from "./answerTypes";
import { QuestionRenderer } from "./QuestionRenderer";
import { evaluateConditionalDisplay } from "@/hooks/useConditionEvaluation";

// Extended type: AssessmentQuestion union + our composite metadata
type CompositeGroupQuestion = AssessmentQuestion & {
  _questionGroup?: string | null;
  _excludeMatch?: { depends_on: string; message?: string };
};

interface CompositeQuestionGroupProps {
  /** All questions in this group, sorted by display_order */
  questions: CompositeGroupQuestion[];
  answers: AnswerData;
  onAnswerChange: (questionId: string, value: QuestionAnswer) => void;
  onNext: () => void;
  onFinish: () => void;
}

/**
 * Renders a composite group of questions on a single screen.
 * - First question acts as the group title/primary question
 * - Sub-questions are rendered below, respecting their own conditional display
 * - Supports mutual exclusion via _excludeMatch: Part B hides the option chosen in Part A
 */
export function CompositeQuestionGroup({
  questions,
  answers,
  onAnswerChange,
  onNext,
  onFinish,
}: CompositeQuestionGroupProps) {
  if (!questions.length) return null;

  const [primary, ...subQuestions] = questions;

  return (
    <div className="space-y-8 w-full max-w-4xl mx-auto py-4">
      {/* Primary question */}
      <QuestionRenderer
        question={primary as AssessmentQuestion}
        answers={answers}
        onAnswerChange={onAnswerChange}
        onNext={onNext}
        onFinish={onFinish}
      />

      {/* Sub-questions */}
      {subQuestions.map((q) => {
        // Evaluate sub-question's own conditional display
        const cond = (q as any).conditionalDisplay;
        if (cond) {
          const visible = evaluateConditionalDisplay(cond, answers as Record<string, any>);
          if (!visible) return null;
        }

        // Apply mutual exclusion (filter out excluded options for Part B)
        const filteredQ = applyMutualExclusion(q, answers);

        return (
          <div key={(q as any).id} className="border-t border-border pt-6">
            <QuestionRenderer
              question={filteredQ as AssessmentQuestion}
              answers={answers}
              onAnswerChange={onAnswerChange}
              onNext={onNext}
              onFinish={onFinish}
            />
          </div>
        );
      })}
    </div>
  );
}

/**
 * Applies mutual exclusion: removes the Part A option from Part B's available options.
 * Returns a new question object with the excluded option marked as disabled.
 */
function applyMutualExclusion(
  question: CompositeGroupQuestion,
  answers: AnswerData
): CompositeGroupQuestion {
  const rule = (question as any)._excludeMatch;
  if (!rule) return question;

  const dependsOnAnswer = answers[rule.depends_on];
  const excludedValue = isRadioAnswer(dependsOnAnswer) ? dependsOnAnswer : null;
  if (!excludedValue) return question;

  // Only applicable to radio/single_select questions
  if ((question as any).type !== "radio") return question;

  const options = (question as any).options;
  if (!options) return question;

  return {
    ...question,
    options: options.filter((opt: any) => opt.value !== excludedValue),
  } as CompositeGroupQuestion;
}
